import "./SearchHistory.scss";
import React, { useEffect, useCallback, useState, useMemo } from "react";
import { Box } from "../../../components/styled-system/Box";
import Pagination from "react-bootstrap/Pagination";
import { Spinner } from "react-bootstrap";
import Table from "react-bootstrap/Table";
import { getSearchHistory } from "../../../api/old-vine-registry-api";
import { SearchHistory, SearchHistoryResultPage } from "../../../api/models";

const pageSize = 50;

// Custom hook to load search history with optional filter
function useSearchHistoryLoader() {
  const [searchHistory, setSearchHistory] = useState<SearchHistory[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async (page = 0) => {
    setLoading(true);
    const params = {
      order: "desc",
      pageNumber: page,
      pageSize,
    }

    const result: SearchHistoryResultPage = await getSearchHistory(params);
    setSearchHistory(result.content || []);
    setTotalResults(result.totalElements);
    setTotalPages(result.totalPages);
    setCurrentPage(page);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData(0);
  }, [loadData]);

  const nextPage = () => currentPage < totalPages - 1 && loadData(currentPage + 1);
  const prevPage = () => currentPage > 0 && loadData(currentPage - 1);
  const firstPage = () => loadData(0);
  const lastPage = () => loadData(totalPages - 1);

  return {
    loading,
    data: searchHistory,
    totalResults,
    totalPages,
    currentPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
  };
}

// Helper: Top N most frequent search strings
function topNSearches(data: SearchHistory[], n: number) {
  const freq: Record<string, number> = {};
  data.forEach(row => {
    freq[row.searchString] = (freq[row.searchString] || 0) + 1;
  });
  return Object.entries(freq)
    .sort(([, a], [, b]) => b - a)
    .slice(0, n)
    .map(([searchString, count]) => ({ searchString, count }));
}

// Helper: Latest unique N searches with zero results
function latestNoResults(data: SearchHistory[], n: number) {
  const zeroHits = data
    .filter(r => r.resultCount === 0)
    .sort((a, b) => new Date(b.date!).getTime() - new Date(a.date!).getTime());

  // preserve latest unique by searchString
  const map = new Map<string, SearchHistory>();
  zeroHits.forEach(r => {
    if (!map.has(r.searchString)) {
      map.set(r.searchString, r);
    }
  });

  return Array.from(map.values())
    .slice(0, n)
    .map(r => ({ searchString: r.searchString, date: r.date! }));
}

export const SearchHistoryTable: React.FC = () => {

  const {
    loading,
    data: tableData,
    totalPages,
    currentPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
  } = useSearchHistoryLoader();

  // Client-side numeric filters
  const filteredData = useMemo(
    () => tableData,
    [tableData]
  );

  // Date buckets
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();

  const monthData = useMemo(
    () => tableData.filter(r => {
      const d = new Date(r.date!);
      return d.getFullYear() === year && d.getMonth() === month;
    }),
    [tableData, year, month]
  );

  const yearData = useMemo(
    () => tableData.filter(r => new Date(r.date!).getFullYear() === year),
    [tableData, year]
  );

  const top10ThisMonth = useMemo(() => topNSearches(monthData, 10), [monthData]);
  const top10ThisYear  = useMemo(() => topNSearches(yearData, 10),  [yearData]);
  const latest10NoRes  = useMemo(() => latestNoResults(tableData, 10), [tableData]);

  useEffect(() => {
    document.title = "Old Vine Search History";
    return () => { document.title = "Old Vine Registry"; };
  }, []);

  return (
    <div className="content-panel">
      <h2 className="title-medium">Search History Summary</h2>

      {/* Summary panels */}
      <Box className="summary-view">
        <Box>
          <h2 className="title-small">Top 10 - This Month</h2>
          <ul className="input-text u-mt-large">
            {top10ThisMonth.map(item => (
              <li key={item.searchString}>
                {item.searchString} ({item.count})
              </li>
            ))}
          </ul>
        </Box>

        <Box>
          <h2 className="title-small">Top 10 - This Year</h2>
          <ul className="input-text u-mt-large">
            {top10ThisYear.map(item => (
              <li key={item.searchString}>
                {item.searchString} ({item.count})
              </li>
            ))}
          </ul>
        </Box>

        <Box>
          <h2 className="title-small">Latest 10 No-Results</h2>
            <ul className="input-text u-mt-large">
            {latest10NoRes.map(item => (
              <li key={item.searchString + item.date}>
                {item.searchString} — {new Date(item.date).toLocaleDateString()}
              </li>
            ))}
          </ul>
        </Box>
      </Box>

      {/* Filters 
      <Box display="flex" alignItems="center" mt="16px" mb="16px" flexWrap="wrap">
        <input
          type="text"
          placeholder="Search text..."
          value={filterString}
          onChange={e => setFilterString(e.target.value)}
          className="input-text"
        />
        <input
          type="text"
          placeholder="Min"
          value={minResults}
          onChange={e => setMinResults(e.target.value)}
          className="input-text search-input"
          style={{ width: '60px' }}
        />
        <input
          type="text"
          placeholder="Max"
          value={maxResults}
          onChange={e => setMaxResults(e.target.value)}
          className="input-text search-input"
          style={{ width: '60px' }}
        />
      </Box> */}
      <h2 className="title-medium">Search History</h2>
      {/* Table */}
      
      {loading && <Box height="25px" mt="20px" display="flex" justifyContent="center" alignItems="center"> 
                    <Spinner animation="border" role="status" />
                  </Box>
      }

      <Table hover size="sm" className="vineyard-table input-text u-mt-large">
        <thead>
          <tr>
            <th>Date</th>
            <th>Search String</th>
            <th>Records</th>
          </tr>
        </thead>
        {!loading && filteredData.length > 0 && (
          <tbody>
            {filteredData.map(row => (
              <tr key={row.id}>
                <td>{new Date(row.date!).toLocaleString()}</td>
                <td>{row.searchString}</td>
                <td>{row.resultCount}</td>
              </tr>
            ))}
          </tbody>
        )}
      </Table>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box display="flex" alignItems="center" mt="20px">
          <span className="description-text">
            Page {currentPage + 1} of {totalPages}
          </span>
          <Pagination className="u-ml-medium">
            <Pagination.First onClick={firstPage} disabled={currentPage === 0} />
            <Pagination.Prev onClick={prevPage} disabled={currentPage === 0} />
            <Pagination.Next onClick={nextPage} disabled={currentPage === totalPages - 1} />
            <Pagination.Last onClick={lastPage} disabled={currentPage === totalPages - 1} />
          </Pagination>
        </Box>
      )}
    </div>
  );
};